import React, { useCallback, useState } from 'react';
import { X } from 'lucide-react';
import { normalizePostcodeToken, splitPostcodeInput } from '../../utils/postcodeTokens';

export interface PostcodeChipInputProps {
  chips: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  'aria-label'?: string;
}

export const PostcodeChipInput: React.FC<PostcodeChipInputProps> = ({
  chips,
  onChange,
  disabled = false,
  placeholder = 'Type postcode and press Space or Enter',
  id,
  'aria-label': ariaLabel,
}) => {
  const [draft, setDraft] = useState('');

  const commitDraft = useCallback(() => {
    const parts = splitPostcodeInput(draft);
    if (parts.length === 0) {
      setDraft('');
      return;
    }
    const next = [...chips];
    const seen = new Set(next);
    for (const p of parts) {
      const norm = normalizePostcodeToken(p);
      if (norm && !seen.has(norm)) {
        seen.add(norm);
        next.push(norm);
      }
    }
    onChange(next);
    setDraft('');
  }, [chips, draft, onChange]);

  const removeChip = (index: number) => {
    onChange(chips.filter((_, i) => i !== index));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ' || e.key === ',') {
      e.preventDefault();
      commitDraft();
      return;
    }
    if (e.key === 'Backspace' && draft === '' && chips.length > 0) {
      e.preventDefault();
      onChange(chips.slice(0, -1));
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    const text = e.clipboardData.getData('text');
    if (!text?.trim()) {
      return;
    }
    const parts = splitPostcodeInput(text);
    if (parts.length <= 1 && !text.includes('\n') && !text.includes(',')) {
      return;
    }
    e.preventDefault();
    const next = [...chips];
    const seen = new Set(next);
    for (const p of parts) {
      const norm = normalizePostcodeToken(p);
      if (norm && !seen.has(norm)) {
        seen.add(norm);
        next.push(norm);
      }
    }
    onChange(next);
    setDraft('');
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-2 min-h-[42px] w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-teal-500 focus-within:border-teal-500 ${
        disabled ? 'opacity-60 cursor-not-allowed' : ''
      }`}
    >
      {chips.map((c, i) => (
        <span
          key={`${c}-${i}`}
          className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-0.5 text-sm font-medium text-teal-900"
        >
          {c}
          <button
            type="button"
            disabled={disabled}
            onClick={() => removeChip(i)}
            className="rounded-full p-0.5 hover:bg-teal-100 disabled:opacity-50"
            aria-label={`Remove ${c}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      ))}
      <input
        id={id}
        type="text"
        disabled={disabled}
        value={draft}
        onChange={(e) => setDraft(e.target.value.toUpperCase())}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={() => {
          if (draft.trim()) {
            commitDraft();
          }
        }}
        className="flex-1 min-w-[120px] border-0 bg-transparent text-sm outline-none py-1"
        placeholder={chips.length === 0 ? placeholder : ''}
        aria-label={ariaLabel ?? 'Postcode entry'}
      />
    </div>
  );
};
